import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  IconButton,
  useToast,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Checkbox,
  Icon,
  Tooltip,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { ICON_MAP } from '../../utils/iconMap';
import { useMenuTitle } from '../../hooks/useMenuTitle';
import { FaQuestionCircle } from 'react-icons/fa';

export default function RolePermissions() {
  const menuTitle = useMenuTitle();
  const supabase = useSupabaseClient();
  const toast = useToast();
  const { 
    isOpen: isHelpOpen, 
    onOpen: onHelpOpen, 
    onClose: onHelpClose 
  } = useDisclosure();

  const [roles, setRoles] = useState([]);
  const [menuGroups, setMenuGroups] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [roleMenuItems, setRoleMenuItems] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [loading, setLoading] = useState(true);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [rolesResult, groupsResult, itemsResult, roleItemsResult] = await Promise.all([
        supabase.from('roles').select('*').order('name'),
        supabase.from('menu_groups').select('*').order('display_order'),
        supabase.from('menu_items').select('*').order('display_order'),
        supabase.from('role_menu_items').select('*')
      ]);

      if (rolesResult.error) throw rolesResult.error;
      if (groupsResult.error) throw groupsResult.error;
      if (itemsResult.error) throw itemsResult.error;
      if (roleItemsResult.error) throw roleItemsResult.error;

      // Create a mapping of role_id -> menu_item_id[] for easy access
      const roleItemMapping = {};
      roleItemsResult.data.forEach(item => {
        if (!roleItemMapping[item.role_id]) {
          roleItemMapping[item.role_id] = [];
        }
        roleItemMapping[item.role_id].push(item.menu_item_id);
      });

      setRoles(rolesResult.data);
      setMenuGroups(groupsResult.data);
      setMenuItems(itemsResult.data);
      setRoleMenuItems(roleItemMapping);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error fetching data',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Toggle menu item for role
  const toggleMenuItem = async (roleId, menuItemId) => {
    try {
      const isAssigned = roleMenuItems[roleId]?.includes(menuItemId);
      
      if (isAssigned) {
        // Remove assignment
        const { error } = await supabase
          .from('role_menu_items')
          .delete()
          .eq('role_id', roleId)
          .eq('menu_item_id', menuItemId);

        if (error) throw error;
      } else {
        // Add assignment
        const { error } = await supabase
          .from('role_menu_items')
          .insert({ role_id: roleId, menu_item_id: menuItemId });

        if (error) throw error;
      }

      await fetchData();
    } catch (error) {
      console.error('Error toggling menu item:', error);
      toast({
        title: 'Error updating menu access',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Toggle all items in a group for a role
  const toggleGroupItems = async (roleId, groupId, shouldCheck) => {
    try {
      const groupMenuItems = menuItems.filter(item => item.menu_group_id === groupId);
      const updates = groupMenuItems.map(item => ({
        role_id: roleId,
        menu_item_id: item.id
      }));

      if (shouldCheck) {
        // Add all missing assignments
        const { error } = await supabase
          .from('role_menu_items')
          .upsert(updates);

        if (error) throw error;
      } else {
        // Remove all assignments for this group
        const { error } = await supabase
          .from('role_menu_items')
          .delete()
          .eq('role_id', roleId)
          .in('menu_item_id', groupMenuItems.map(item => item.id));

        if (error) throw error;
      }

      await fetchData();
    } catch (error) {
      console.error('Error toggling group items:', error);
      toast({
        title: 'Error updating menu access',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    }
  };

  // Render help modal
  const renderHelpModal = () => (
    <Modal isOpen={isHelpOpen} onClose={onHelpClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxH="600px">
        <ModalHeader>Role Permissions Help</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4} align="stretch">
            <Text>
              Role Permissions allows you to control which menu items each role can access.
              Use the checkboxes to grant or revoke access to specific menu items for each role.
            </Text>

            <Text fontWeight="semibold">Features:</Text>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>• Grant or revoke access to individual menu items</Text>
              <Text>• Toggle all items in a menu group at once</Text>
              <Text>• View current permissions for all roles</Text>
              <Text>• Changes take effect immediately</Text>
            </VStack>

            <Text fontWeight="semibold">Important Notes:</Text>
            <VStack spacing={2} align="stretch" pl={4}>
              <Text>• SuperAdmin role always has access to all menu items</Text>
              <Text>• Changes affect all users with the modified role</Text>
              <Text>• Removing access will hide menu items from users</Text>
              <Text>• Users must log out and back in to see changes</Text>
            </VStack>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  return (
    <Box p={4}>
      <VStack spacing={4} align="stretch">
        <HStack justify="space-between" mb={2} align="center">
          <Text fontSize="2xl" fontWeight="bold">{menuTitle}</Text>
          <HStack spacing={4}>
            <Text fontSize="sm" color="gray.500" fontStyle="italic">
              Note: SuperAdmin role always has access to all menu items
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const allExpanded = menuGroups.every(group => expandedGroups[group.id]);
                const newState = !allExpanded;
                const updates = {};
                menuGroups.forEach(group => {
                  updates[group.id] = newState;
                });
                setExpandedGroups(updates);
              }}
            >
              {menuGroups.every(group => expandedGroups[group.id]) ? 'Collapse All' : 'Expand All'}
            </Button>
            <Tooltip label="Help" placement="left">
              <IconButton
                icon={<Icon as={FaQuestionCircle} />}
                variant="ghost"
                onClick={onHelpOpen}
                aria-label="Help"
              />
            </Tooltip>
          </HStack>
        </HStack>

        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                <Th py={2}>Menu Item</Th>
                {roles.map(role => (
                  <Th key={role.id} py={2}>{role.name}</Th>
                ))}
              </Tr>
            </Thead>
            <Tbody>
              {menuGroups.map(group => (
                <React.Fragment key={group.id}>
                  {/* Group header */}
                  <Tr bg="gray.50">
                    <Td py={1} fontWeight="bold">
                      <HStack spacing={1}>
                        <IconButton
                          icon={expandedGroups[group.id] ? <ChevronUpIcon /> : <ChevronDownIcon />}
                          size="xs"
                          variant="ghost"
                          onClick={() => setExpandedGroups(prev => ({
                            ...prev,
                            [group.id]: !prev[group.id]
                          }))}
                        />
                        {ICON_MAP[group.icon] && (
                          <Icon 
                            as={ICON_MAP[group.icon]} 
                            boxSize={4} 
                            color="gray.600"
                          />
                        )}
                        <Text>{group.name}</Text>
                      </HStack>
                    </Td>
                    {roles.map(role => (
                      <Td key={role.id} py={1}>
                        <Checkbox
                          size="sm"
                          isChecked={role.name.toLowerCase() === 'superadmin' ? true : menuItems
                            .filter(item => item.menu_group_id === group.id)
                            .every(item => roleMenuItems[role.id]?.includes(item.id))
                          }
                          onChange={(e) => {
                            if (role.name.toLowerCase() !== 'superadmin') {
                              toggleGroupItems(role.id, group.id, e.target.checked);
                            }
                          }}
                          isDisabled={role.name.toLowerCase() === 'superadmin'}
                        />
                      </Td>
                    ))}
                  </Tr>
                  {/* Menu items */}
                  {expandedGroups[group.id] && menuItems
                    .filter(item => item.menu_group_id === group.id)
                    .map(item => (
                      <Tr key={item.id}>
                        <Td py={1} pl={8}>
                          <Tooltip label={`Path: ${item.path}`} placement="right">
                            <HStack spacing={2}>
                              {ICON_MAP[item.icon] && (
                                <Icon 
                                  as={ICON_MAP[item.icon]} 
                                  boxSize={3.5} 
                                  color="gray.500"
                                />
                              )}
                              <Text>{item.name}</Text>
                            </HStack>
                          </Tooltip>
                        </Td>
                        {roles.map(role => (
                          <Td key={role.id} py={1}>
                            <Checkbox
                              size="sm"
                              isChecked={role.name.toLowerCase() === 'superadmin' ? true : roleMenuItems[role.id]?.includes(item.id)}
                              onChange={() => {
                                if (role.name.toLowerCase() !== 'superadmin') {
                                  toggleMenuItem(role.id, item.id);
                                }
                              }}
                              isDisabled={role.name.toLowerCase() === 'superadmin'}
                            />
                          </Td>
                        ))}
                      </Tr>
                    ))}
                </React.Fragment>
              ))}
            </Tbody>
          </Table>
        </Box>

        {/* Help Modal */}
        {renderHelpModal()}
      </VStack>
    </Box>
  );
}
